import type { Session } from '@supabase/supabase-js';
import { esErrorDeRed } from '@/core/red/conectividad';

/**
 * La credencial que permite seguir trabajando sin señal.
 *
 * ## Por qué existe
 *
 * El token de acceso de Supabase dura una hora. Pasada esa hora `getSession()`
 * intenta renovarlo, y sin red eso falla: devuelve `session: null` y la
 * aplicación manda a Login. En el campo eso es fatal —no hay forma de volver a
 * entrar— y es exactamente donde la aplicación tiene que servir.
 *
 * ## Qué NO es
 *
 * Esto no autentica a nadie. No abre ningún dato nuevo: lo que se ve sin señal
 * ya está descargado en el dispositivo. No toca RLS ni afloja el servidor, que
 * sigue rechazando cualquier operación con un token vencido. Lo único que hace
 * es dejar la interfaz utilizable y la cola andando mientras no hay red.
 *
 * ## Las tres condiciones
 *
 * No se entra sin conexión por el solo hecho de que `getSession()` haya dado
 * `null`. Hacen falta las tres:
 *
 *  1. **hubo una autenticación online válida antes en ESTE dispositivo**. Sin
 *     eso no hay nada que recordar y el `null` significa lo que parece;
 *  2. **no se cerró sesión**. `Salir` borra esta credencial ANTES de intentar
 *     avisarle al servidor, así que funciona igual sin señal;
 *  3. **no venció la gracia**: siete días desde la última autenticación online
 *     satisfactoria. Después de eso hay que conectarse.
 *
 * La gracia no es una comodidad: es lo que impide que un teléfono perdido siga
 * abriendo la aplicación para siempre. Siete días cubre una recorrida larga sin
 * señal; un mes ya no es «sin señal», es «no se usa».
 */

/** Días que el dispositivo puede trabajar sin volver a autenticarse online. */
export const GRACIA_OFFLINE_DIAS = 7;

const CLAVE = 'gv.sesionOffline';

/**
 * Cuándo fue el último ingreso online en este dispositivo. **Nunca se borra.**
 *
 * Es una marca de tiempo y nada más: no sirve para entrar, no identifica a
 * nadie, no vale como credencial. Existe para responder una sola pregunta que
 * sin ella es imposible: cuando no hay credencial, ¿es que nunca la hubo, o es
 * que había y desapareció?
 *
 * `sin credencial` dice lo mismo en los dos casos, y son opuestos: uno significa
 * «falta entrar una vez con señal» y el otro «algo la está borrando». Esa
 * ambigüedad costó varias vueltas de diagnóstico, cada una gastando una prueba
 * en el teléfono de otra persona.
 *
 * No se borra ni al cerrar sesión: lo que responde es sobre el APARATO, no
 * sobre quien lo usa, y perderla ahí volvería a dejar la misma duda.
 */
const CUANDO_ENTRE = 'gv.ultimoIngresoOnline';

/**
 * Quién borró la credencial la última vez, y cuándo. **Tampoco se borra.**
 *
 * Hay dos lugares en toda la aplicación que la destruyen, y los dos están
 * justificados. Aun así la credencial seguía desapareciendo en el teléfono
 * después de tres arreglos, y desde acá no hay forma de saber cuál de los dos
 * fue: el efecto es idéntico.
 *
 * Razonar sobre el código ya falló tres veces. Esto lo anota.
 */
const QUIEN_BORRO = 'gv.ultimoBorrado';

/** Los únicos que pueden destruirla. Nombrarlos obliga a declararse. */
export type QuienBorra = 'salir' | 'revalidacion';

interface Guardada {
  /** La última sesión que el servidor dio por buena. */
  session: Session;
  /** Cuándo fue esa autenticación online. Es el reloj de la gracia. */
  autenticadoEn: number;
  /** De quién es, para no reabrir con la sesión de otro. */
  userId: string;
}

/**
 * Por qué no se puede entrar sin conexión. Se cuenta, no se calla.
 *
 * Son dos y no tres. «Nunca hubo sesión» y «se cerró sesión» terminan en el
 * mismo lugar —no hay credencial guardada— y no se distinguen mirando el
 * almacén. Tener un tercer valor que nunca se emite es una promesa que el
 * código no cumple, y de esas se sospecha justo cuando hace falta confiar.
 */
export type MotivoRechazo =
  /** No hay credencial local: nunca la hubo, o `Salir` la borró. */
  | 'sin-sesion-previa'
  /** Pasaron más de `GRACIA_OFFLINE_DIAS` desde la última autenticación. */
  | 'gracia-vencida';

export type Resultado =
  { ok: true; session: Session; autenticadoEn: number } | { ok: false; motivo: MotivoRechazo };

function leerCruda(): Guardada | null {
  try {
    const texto = localStorage.getItem(CLAVE);
    if (!texto) return null;
    const g = JSON.parse(texto) as Guardada;
    // Un registro a medias es un registro roto: se trata como si no estuviera.
    if (!g?.session?.access_token || !g.userId || typeof g.autenticadoEn !== 'number') return null;
    return g;
  } catch {
    // Almacenamiento bloqueado, JSON corrupto, cuota llena. En todos los casos
    // la respuesta correcta es la misma: no hay credencial local.
    return null;
  }
}

/**
 * Registrar que el servidor autenticó a esta persona recién.
 *
 * Se llama con CADA sesión que llega del servidor, no sólo al ingresar: cada
 * refresco exitoso corre el reloj de la gracia siete días más. Alguien que usa
 * la aplicación con señal una vez por semana nunca la ve vencer.
 */
export function guardarAutenticacionOnline(session: Session): void {
  try {
    const g: Guardada = {
      session,
      autenticadoEn: Date.now(),
      userId: session.user.id,
    };
    localStorage.setItem(CLAVE, JSON.stringify(g));
    localStorage.setItem(CUANDO_ENTRE, String(g.autenticadoEn));
  } catch {
    // Sin almacenamiento no hay modo sin conexión, y no es motivo para romper
    // la sesión que SÍ está funcionando ahora mismo.
  }
}

/**
 * Borrar la credencial local.
 *
 * Es lo primero que hace `Salir`, antes de hablarle al servidor: si se hiciera
 * después, cerrar sesión sin señal fallaría y el teléfono seguiría abriendo la
 * aplicación como la persona anterior.
 */
export function borrarSesionOffline(quien: QuienBorra): void {
  try {
    localStorage.removeItem(CLAVE);
    localStorage.setItem(QUIEN_BORRO, `${quien}|${Date.now()}`);
  } catch {
    // Nada que hacer. El caso importa tan poco como suena: sin almacenamiento
    // tampoco se guardó nada que borrar.
  }
}

/** Cuándo vence la gracia de lo guardado, o `null` si no hay nada guardado. */
export function venceLaGracia(ahora = Date.now()): number | null {
  const g = leerCruda();
  if (!g) return null;
  void ahora;
  return g.autenticadoEn + GRACIA_OFFLINE_DIAS * 24 * 60 * 60 * 1000;
}

/**
 * ¿Se puede seguir trabajando sin señal, y con qué sesión?
 *
 * Devuelve el motivo cuando no, para poder decirlo en pantalla. «Volvé a
 * conectarte para seguir usando la aplicación» es accionable; una pantalla de
 * Login sin explicación, no.
 */
export function sesionOfflineUsable(ahora = Date.now()): Resultado {
  const g = leerCruda();
  // No se distingue «nunca hubo» de «se cerró sesión» mirando el almacén: en
  // los dos casos no hay nada. Y está bien que no se distinga, porque la
  // respuesta es la misma. El motivo se afina desde afuera, cuando quien
  // pregunta sabe que acaba de cerrar sesión.
  if (!g) return { ok: false, motivo: 'sin-sesion-previa' };

  const vence = g.autenticadoEn + GRACIA_OFFLINE_DIAS * 24 * 60 * 60 * 1000;
  if (ahora > vence) return { ok: false, motivo: 'gracia-vencida' };

  return { ok: true, session: g.session, autenticadoEn: g.autenticadoEn };
}

/**
 * En qué estado está la credencial, en una línea para mostrar.
 *
 * ## Por qué existe
 *
 * Porque razonando sobre el código no alcanzó. La credencial se escribe en tres
 * lugares y se borra en dos, todos revisados uno por uno y todos correctos, y
 * en el teléfono seguía sin estar. Cuando el código dice una cosa y el aparato
 * otra, hay que preguntarle al aparato.
 *
 * Con esto el diagnóstico se parte en dos mitades que se miran por separado:
 *
 *  - **Después de entrar con señal**, en Mi cuenta. Si acá dice «no hay», la
 *    credencial no se está escribiendo.
 *  - **Al abrir sin señal**, en la pantalla de ingreso. Si allá decía «guardada»
 *    y acá dice «no hay», algo la borra en el medio.
 *
 * Son dos fallas distintas con arreglos distintos, y hasta ahora no había forma
 * de saber cuál de las dos era.
 */
export function estadoDeLaCredencial(ahora = Date.now()): string {
  let crudo: string | null;
  try {
    crudo = localStorage.getItem(CLAVE);
  } catch {
    // El almacén no se deja leer. Es distinto de estar vacío y se arregla
    // distinto, así que se dice distinto.
    return 'credencial ilegible';
  }

  if (crudo === null) {
    // Acá está la diferencia que importa: «nunca entraste en este teléfono» y
    // «entraste y la credencial desapareció» son problemas opuestos, y hasta
    // ahora los dos se veían igual.
    const antes = Number(localStorage.getItem(CUANDO_ENTRE));
    if (!antes) return 'sin credencial · nunca entré acá';

    // Quién se la llevó. Es el dato que faltaba: los dos lugares que la borran
    // dejan el mismo resultado, y sin esto no hay forma de saber cuál fue.
    const borrado = localStorage.getItem(QUIEN_BORRO);
    if (borrado) {
      const [quien, cuando] = borrado.split('|');
      return `entré hace ${hace(ahora - antes)} · borrada por ${quien} hace ${hace(ahora - Number(cuando))}`;
    }
    return `sin credencial · entré hace ${hace(ahora - antes)} · nadie la borró`;
  }

  const g = leerCruda();
  // Hay algo escrito pero no pasa la validación: un registro a medias, de una
  // versión vieja, o de otra cosa que usó la misma clave. No es lo mismo que no
  // haber nada, y se arregla distinto.
  if (!g) return 'credencial rota';

  return `credencial de hace ${hace(ahora - g.autenticadoEn)}`;
}

/** Un lapso en palabras cortas, que entren en una línea de pantalla. */
function hace(ms: number): string {
  const minutos = Math.max(0, Math.round(ms / 60000));
  if (minutos < 60) return `${minutos} min`;
  const horas = Math.round(minutos / 60);
  if (horas < 48) return `${horas} h`;
  return `${Math.round(horas / 24)} d`;
}

/**
 * ¿El SERVIDOR rechazó la sesión, o simplemente no se pudo preguntar?
 *
 * ## Por qué hace falta distinguirlo con tanto cuidado
 *
 * Es la condición que decide si se borra la credencial que permite abrir la
 * aplicación sin señal. Equivocarse para un lado deja una sesión muerta abierta
 * hasta siete días, con el servidor rechazando cada pedido. Equivocarse para el
 * otro deja a alguien afuera en el campo, sin señal para poder volver a entrar.
 * No son comparables.
 *
 * ## Los dos intentos anteriores, y por qué fallaron
 *
 * El primero fue `if (error)` a secas: cualquier error borraba. Bastaba un
 * evento `online` mentiroso —el WebView de Android los emite sin que haya red—
 * para perderla.
 *
 * El segundo fue `if (error && !esErrorDeRed(error))`. Mejor, y todavía mal:
 * sin señal `refreshSession()` no devuelve un fallo de red sino **«Auth session
 * missing»**, porque Supabase tampoco tiene sesión en memoria que refrescar.
 * Eso no es un rechazo de nadie —no hubo con quién hablar— pero no parecía un
 * error de red, así que la credencial se borraba igual.
 *
 * ## La regla, ahora al revés
 *
 * No se busca motivo para conservarla: se exige **prueba positiva** de que el
 * servidor contestó y dijo que no. Sin una respuesta HTTP no hubo servidor, y
 * sin servidor nadie rechazó nada.
 */
export function elServidorRechazoLaSesion(error: unknown): boolean {
  if (error == null) return false;

  // No se pudo salir a la red: nadie contestó.
  if (esErrorDeRed(error)) return false;

  const mensaje = String((error as { message?: string })?.message ?? '').toLowerCase();

  // No había sesión local que refrescar. Es exactamente el arranque sin señal,
  // y decirle «rechazo» a esto es lo que costó una semana.
  if (mensaje.includes('session missing') || mensaje.includes('session_not_found')) return false;

  // Sin código de estado no hubo respuesta HTTP. Ante la duda se conserva: la
  // gracia de siete días ya le pone techo a una credencial que sobrevive de
  // más, y no hay techo para alguien que queda afuera en el campo.
  const estado = (error as { status?: number })?.status;
  if (typeof estado !== 'number') return false;

  return estado >= 400 && estado < 500;
}
